import { useState } from 'react';

import { useAuthStore } from '@/stores/auth-store';

type Theme = 'light' | 'dark' | 'system';

const THEME_OPTIONS: { value: Theme; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

const labelBase =
  'block field-label mb-1';

const inputBase =
  'bg-[var(--color-elevated)] border border-[var(--color-border)] rounded text-[var(--color-text-primary)] text-body-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)] transition-colors duration-100';

export function PreferencesPage() {
  const preferences = useAuthStore((s) => s.preferences);
  const updatePreferences = useAuthStore((s) => s.updatePreferences);
  const theme: Theme = preferences?.theme ?? 'system';

  const [notificationsEnabled, setNotificationsEnabled] = useState(
    preferences?.notificationsEnabled ?? false,
  );
  const [notificationLeadDays, setNotificationLeadDays] = useState(
    preferences?.notificationLeadDays ?? 3,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleThemeChange(t: Theme) {
    await updatePreferences({ theme: t });
  }

  async function handleSave() {
    setIsSaving(true);
    setError(null);
    try {
      await updatePreferences({ notificationsEnabled, notificationLeadDays });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center gap-2">
        <h1 className="font-display text-display-sm text-[var(--color-text-display)]">
          Preferences
        </h1>
      </div>

      {/* Theme */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4">
        <h2 className="field-label mb-4">
          Appearance
        </h2>
        <div>
          <span className={labelBase}>Theme</span>
          <div
            role="radiogroup"
            aria-label="Theme"
            className="flex gap-1 bg-[var(--color-bg)] rounded-lg p-1"
          >
            {THEME_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={theme === value}
                onClick={() => void handleThemeChange(value)}
                className={`flex-1 py-1.5 rounded-md text-body-sm font-medium transition-colors duration-100 ${
                  theme === value
                    ? 'bg-[var(--color-accent)] text-white'
                    : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Notifications */}
      <section className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg p-4 space-y-4">
        <h2 className="field-label">
          Notifications
        </h2>
        <p className="text-body-xs text-[var(--color-text-muted)]">
          Push notifications are coming in a future update. Set your preferences now.
        </p>

        {/* Enable toggle */}
        <div className="flex items-center justify-between">
          <div>
            <p className="text-body-sm text-[var(--color-text-primary)] font-medium">
              Enable notifications
            </p>
            <p className="text-body-xs text-[var(--color-text-muted)]">
              Get reminders before tasks are due
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={notificationsEnabled}
            onClick={() => setNotificationsEnabled((prev) => !prev)}
            // M-08 (Defect Batch M): inward outline so focus ring is never clipped (ADR-035).
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 focus:outline-2 focus:outline-[var(--color-accent)] focus:outline-offset-[-2px] ${
              notificationsEnabled ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-elevated)]'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform duration-200 ${
                notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Lead days */}
        <div>
          <label htmlFor="prefs-leadDays" className={labelBase}>
            Notify me this many days before a task is due
          </label>
          <input
            id="prefs-leadDays"
            type="number"
            min={1}
            max={30}
            value={notificationLeadDays}
            onChange={(e) => {
              const val = parseInt(e.target.value, 10);
              if (!isNaN(val)) setNotificationLeadDays(val);
            }}
            disabled={!notificationsEnabled}
            className={`${inputBase} w-24 disabled:opacity-40`}
          />
        </div>

        {/* Default property — Phase 2 placeholder */}
        <div>
          <label htmlFor="prefs-defaultProperty" className={labelBase}>
            Default property
          </label>
          <select
            id="prefs-defaultProperty"
            disabled
            className={`${inputBase} w-full opacity-60 cursor-not-allowed`}
          >
            <option value="">Add properties in the Properties section</option>
          </select>
          <p className="mt-1 text-body-xs text-[var(--color-text-muted)]">
            Add properties in the Properties section to set a default.
          </p>
        </div>

        {/* Error message */}
        {error !== null && (
          <p className="text-body-sm text-[var(--color-danger)]">{error}</p>
        )}

        {/* Save button */}
        <div className="pt-1">
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={isSaving}
            className="px-4 py-2 bg-[var(--color-accent)] text-white rounded text-body-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saved ? 'Saved!' : isSaving ? 'Saving...' : 'Save preferences'}
          </button>
        </div>
      </section>
    </div>
  );
}
